import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, inr, Money } from "@/lib/api";
import { can, useAuth } from "@/context/AuthContext";
import { DataTable, DateRangeFilter, EntityCombobox, Field, PageHeader, SearchBox, StateSelect } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CrudPage } from "@/components/CrudPage";

const CATEGORIES = ["Hotel", "Vehicle", "Fuel", "Driver", "Advertising", "Office", "Salary",
  "Rent", "Software", "Commission", "Travel", "Food", "Maintenance", "Other"];

function ExpenseForm({ open, onClose, onSaved, editItem }) {
  const [vendors, setVendors] = useState([]);
  const [taxRates, setTaxRates] = useState([0, 5, 12, 18, 28]);
  const [saving, setSaving] = useState(false);
  const empty = { date: new Date().toISOString().slice(0, 10), vendor_id: "", vendor_name: "",
    category: "Other", description: "", invoice_no: "", vendor_gstin: "", vendor_state: "",
    vendor_state_code: "", sac: "", taxable: 0, gst_rate: 0, itc_eligible: false, itc_type: "",
    payment_mode: "Cash", payment_status: "paid", is_personal: false, notes: "" };
  const [form, setForm] = useState(empty);
  useEffect(() => {
    if (open) {
      setForm(editItem ? { ...empty, ...editItem } : empty);
      api.get("/vendors?limit=200").then((r) => setVendors(r.data.items)).catch(() => {});
      api.get("/settings/tax-rates").then((r) => setTaxRates(r.data.filter((t) => t.active).map((t) => t.rate))).catch(() => {});
    }
  }, [open, editItem]);

  const onVendor = (vid) => {
    const v = vendors.find((x) => x.id === vid);
    setForm((f) => ({ ...f, vendor_id: vid, vendor_name: v?.name || f.vendor_name,
      vendor_gstin: v?.gstin || "", vendor_state: v?.state || "", vendor_state_code: v?.state_code || "",
      category: f.category === "Other" && v?.category ? v.category : f.category }));
  };

  const taxable = Number(form.taxable || 0);
  const rate = Number(form.gst_rate || 0);
  const intra = (form.vendor_state_code || "29") === "29";
  const cgst = rate > 0 && !form.is_personal ? (intra ? taxable * rate / 200 : 0) : 0;
  const igst = rate > 0 && !form.is_personal ? (intra ? 0 : taxable * rate / 100) : 0;
  const total = taxable + cgst * 2 + igst;

  const submit = async () => {
    if (!form.description || !form.taxable) return toast.error("Enter description and taxable amount");
    setSaving(true);
    try {
      const r = editItem
        ? await api.put(`/expenses/${editItem.id}`, form)
        : await api.post("/expenses", form);
      (r.data.warnings || []).forEach((w) => toast.warning(w));
      toast.success("Expense saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="expense-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{editItem ? "Edit" : "New"} Expense / Purchase</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Field label="Date" required><Input type="date" value={form.date} className="bg-white border-[#E5E0D8]"
            onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="expense-date" /></Field>
          <div className="col-span-2"><Field label="Vendor (from list)">
            <EntityCombobox testid="expense-vendor" value={form.vendor_id} onChange={onVendor}
              items={[{ value: "", label: "— Type manually below —" }, ...vendors.map((v) => ({ value: v.id, label: v.name, sub: v.gstin || "No GSTIN" }))]}
              placeholder="Select vendor…" /></Field></div>
          <Field label="Vendor Name" required><Input value={form.vendor_name} className="bg-white border-[#E5E0D8]"
            onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} data-testid="expense-vendor-name" /></Field>
          <Field label="Vendor GSTIN"><Input value={form.vendor_gstin} className="bg-white border-[#E5E0D8]"
            onChange={(e) => setForm({ ...form, vendor_gstin: e.target.value.toUpperCase() })} data-testid="expense-vendor-gstin" /></Field>
          <Field label="Vendor State"><StateSelect value={form.vendor_state} testid="expense-vendor-state"
            onChange={(v) => setForm({ ...form, vendor_state: v, vendor_state_code: "" })} /></Field>
          <Field label="Category" required>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="bg-white border-[#E5E0D8]" data-testid="expense-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select></Field>
          <Field label="Vendor Invoice No."><Input value={form.invoice_no} className="bg-white border-[#E5E0D8]"
            onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} data-testid="expense-invoice-no" /></Field>
          <Field label="SAC/HSN"><Input value={form.sac} className="bg-white border-[#E5E0D8]"
            onChange={(e) => setForm({ ...form, sac: e.target.value })} data-testid="expense-sac" /></Field>
          <div className="col-span-2 sm:col-span-3"><Field label="Description" required>
            <Textarea value={form.description} className="bg-white border-[#E5E0D8]"
              onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="expense-description" /></Field></div>
          <Field label="Taxable Amount (₹)" required><Input type="number" min="0" value={form.taxable}
            className="bg-white border-[#E5E0D8]" onChange={(e) => setForm({ ...form, taxable: Number(e.target.value) })}
            data-testid="expense-taxable" /></Field>
          <Field label="GST Rate">
            <Select value={String(form.gst_rate)} onValueChange={(v) => setForm({ ...form, gst_rate: Number(v) })}>
              <SelectTrigger className="bg-white border-[#E5E0D8]" data-testid="expense-gst-rate"><SelectValue /></SelectTrigger>
              <SelectContent>{taxRates.map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
            </Select></Field>
          <Field label="Payment Mode">
            <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
              <SelectTrigger className="bg-white border-[#E5E0D8]" data-testid="expense-payment-mode"><SelectValue /></SelectTrigger>
              <SelectContent>{["Cash", "UPI", "Bank Transfer", "Card", "Other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select></Field>
          <Field label="Payment Status">
            <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
              <SelectTrigger className="bg-white border-[#E5E0D8]" data-testid="expense-payment-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="paid">Paid</SelectItem><SelectItem value="due">Due (payable)</SelectItem></SelectContent>
            </Select></Field>
          <Field label="ITC Type" hint="e.g. Inputs / Input services / Capital goods">
            <Input value={form.itc_type} className="bg-white border-[#E5E0D8]" disabled={!form.itc_eligible}
              onChange={(e) => setForm({ ...form, itc_type: e.target.value })} data-testid="expense-itc-type" /></Field>
          <div className="flex flex-col gap-3 pt-5">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={!!form.itc_eligible} disabled={form.is_personal}
                onCheckedChange={(v) => setForm({ ...form, itc_eligible: !!v })} data-testid="expense-itc-eligible" />
              ITC Eligible (claim input credit)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={!!form.is_personal}
                onCheckedChange={(v) => setForm({ ...form, is_personal: !!v, itc_eligible: v ? false : form.itc_eligible })}
                data-testid="expense-is-personal" />
              Personal / non-business expense
            </label>
          </div>
        </div>
        <div className="bg-[#FAF8F5] border border-[#E5E0D8] rounded-lg p-3 text-sm flex flex-wrap gap-x-6 gap-y-1" data-testid="expense-gst-preview">
          <span>CGST: <b className="num">{inr(cgst)}</b></span>
          <span>SGST: <b className="num">{inr(cgst)}</b></span>
          <span>IGST: <b className="num">{inr(igst)}</b></span>
          <span>Total: <b className="num text-[#0D4F4A]">{inr(total)}</b></span>
          {rate > 0 && !form.vendor_gstin && <span className="text-amber-600 text-xs">⚠ GST without vendor GSTIN — ITC may be disallowed</span>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0D4F4A] hover:bg-[#136A63]" data-testid="expense-submit">
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save Expense
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Expenses() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, pages: 1, page: 1 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [itc, setItc] = useState("");
  const [range, setRange] = useState({ from: "", to: "" });
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const writable = can(user?.role, "expenses", true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/expenses", { params: { q, category, itc, page, limit: 25, from_date: range.from, to_date: range.to } });
      setData(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setLoading(false);
  }, [q, category, itc, page, range]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    if (params.get("new") === "1" && writable) { setEditItem(null); setOpen(true); }
  }, [params, writable]);

  const remove = async (row) => {
    if (!window.confirm("Delete this expense? Ledger entries will be reversed.")) return;
    try {
      await api.delete(`/expenses/${row.id}`);
      toast.success("Expense deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <Tabs defaultValue="expenses">
        <PageHeader title="Expenses & Purchases" subtitle="Business expenses with GST input credit (ITC) tracking"
          testid="expenses-header"
          actions={
            <>
              <TabsList data-testid="expenses-tabs">
                <TabsTrigger value="expenses" data-testid="tab-expenses">Expenses</TabsTrigger>
                <TabsTrigger value="vendors" data-testid="tab-vendors">Vendors</TabsTrigger>
              </TabsList>
              {writable && (
                <Button className="bg-[#0D4F4A] hover:bg-[#136A63]" onClick={() => { setEditItem(null); setOpen(true); }}
                  data-testid="expenses-create-button"><Plus className="h-4 w-4 mr-1" /> Add Expense</Button>
              )}
            </>
          } />
        <TabsContent value="expenses">
          <div className="flex flex-wrap gap-2 mb-4">
            <SearchBox value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search vendor, description, invoice…" testid="expenses-search" />
            <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40 bg-white border-[#E5E0D8]" data-testid="expenses-category-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Categories</SelectItem>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={itc || "all"} onValueChange={(v) => setItc(v === "all" ? "" : v)}>
              <SelectTrigger className="w-32 bg-white border-[#E5E0D8]" data-testid="expenses-itc-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">ITC: All</SelectItem><SelectItem value="yes">ITC Eligible</SelectItem><SelectItem value="no">Not Eligible</SelectItem></SelectContent>
            </Select>
            <DateRangeFilter from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} testid="expenses-date-filter" />
          </div>
          <DataTable testid="expenses-table" loading={loading}
            columns={[
              { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
              { key: "vendor_name", label: "Vendor", render: (r) => (
                <div><div className="font-medium">{r.vendor_name || "—"}{r.is_demo && <span className="ml-1 text-[10px] font-bold text-[#8a6d14]">DEMO</span>}</div>
                  <div className="text-xs text-[#5C6B69]">{r.vendor_gstin || "No GSTIN"}</div></div>) },
              { key: "category", label: "Category" },
              { key: "description", label: "Description", render: (r) => <span className="line-clamp-1 max-w-56">{r.description}</span> },
              { key: "taxable", label: "Taxable", align: "right", render: (r) => <Money value={r.taxable} /> },
              { key: "gst", label: "GST", align: "right", render: (r) => <Money value={(r.cgst || 0) + (r.sgst || 0) + (r.igst || 0)} /> },
              { key: "total", label: "Total", align: "right", render: (r) => <Money value={r.total} className="font-semibold" /> },
              { key: "itc_eligible", label: "ITC", render: (r) => r.is_personal
                ? <span className="text-xs text-slate-500">Personal</span>
                : r.itc_eligible ? <span className="text-xs font-bold text-emerald-600">Eligible</span>
                : <span className="text-xs text-amber-600">No</span> },
              { key: "payment_status", label: "Status", render: (r) => r.payment_status === "due"
                ? <span className="text-xs font-bold text-red-600">Due</span>
                : <span className="text-xs text-emerald-600">Paid</span> },
              ...(writable ? [{ key: "_a", label: "", align: "right", render: (r) => (
                <div className="flex justify-end gap-1">
                  <Button size="sm" variant="ghost" onClick={() => { setEditItem(r); setOpen(true); }} data-testid={`expense-edit-${r.id}`}>Edit</Button>
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={() => remove(r)} data-testid={`expense-delete-${r.id}`}>Delete</Button>
                </div>) }] : []),
            ]}
            rows={data.items} page={data.page} pages={data.pages} total={data.total} onPage={setPage}
            empty="No expenses recorded yet." />
        </TabsContent>
        <TabsContent value="vendors">
          <CrudPage title="Vendors" testid="vendors" endpoint="/vendors" searchPlaceholder="Search vendors…"
            createLabel="New Vendor"
            defaults={{ name: "", gstin: "", phone: "", email: "", state: "", category: "Other", notes: "" }}
            columns={[
              { key: "vendor_id", label: "ID" },
              { key: "name", label: "Name", render: (r) => <span className="font-medium">{r.name}</span> },
              { key: "gstin", label: "GSTIN", render: (r) => r.gstin || "—" },
              { key: "state", label: "State" },
              { key: "category", label: "Category" },
              { key: "phone", label: "Phone" },
            ]}
            fields={[
              { key: "name", label: "Vendor Name", required: true },
              { key: "gstin", label: "GSTIN" },
              { key: "phone", label: "Phone" },
              { key: "email", label: "Email" },
              { key: "state", label: "State", type: "state" },
              { key: "category", label: "Category", type: "select", options: CATEGORIES.map((c) => ({ value: c, label: c })) },
              { key: "notes", label: "Notes", type: "textarea", full: true },
            ]} />
        </TabsContent>
      </Tabs>
      <ExpenseForm open={open} onClose={() => setOpen(false)} onSaved={load} editItem={editItem} />
    </div>
  );
}
